import React, {
    FormEvent,
    KeyboardEvent,
    Suspense,
    useEffect,
    useRef,
    useState,
} from 'react';
import useResizeObserver from 'use-resize-observer';

import {
    attachmentCompose,
    messageCompose,
    prepareAttachment,
    removeQuotedMessage,
    sendChatMessage,
} from 'flow/actions/chat-actions';
import EmojiSearch from 'flow/components/messages/EmojiSearch';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useAutosizeTextArea from 'flow/hooks/useAutosizeTextArea';
import usePremadeAnswers from 'flow/hooks/usePremadeAnswers';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectOwnExtensionKey,
    selectOwnUserHasCapability,
} from 'flow/selectors/ExtensionSelectors';
import {
    ChatMessageKey,
    ChatSessionKey,
    ExtensionKey,
} from 'flow/types/EntityKeys';
import { Capabilities } from 'flow/utils/capability-utils';
import {
    ChatAttachments,
    createMentionDTOsFromText,
    getAttachmentTypeFromExtension,
    MAX_ALLOWED_ATTACHMENT_SIZE,
    replaceWhitespaces,
} from 'flow/utils/message-utils';
import {
    getInitialPremadeAnswerInserterState,
    getNextPremadeAnswerInserterState,
    offerKeyEventToPremadeAnswerInserter,
} from 'flow/utils/premade-answer-utils';
import { isCmdOrCtrlPressed } from 'flow/utils/shortcut-utils';
import { twMerge } from 'tailwind-merge';
import AddButton from './AddButton';
import AttachmentPreview from './AttachmentPreview';
import EmojiPicker from './EmojiPicker';
import {
    getInitialMentionInserterState,
    getNextMentionInserterState,
    MentionInserter,
    offerKeyEventToMessageInserter,
} from './MentionInserter';
import { PremadeAnswerInserter } from './PremadeAnswersInserter';
import PremadeAnswersManager from './PremadeAnswersManager';
import QuotedMessage from './QuotedMessage';
import SendButton from './SendButton';
import type { i18n as I18n } from 'i18next';

export const roomTypes = {
    session: 'session',
    room: 'room',
    public: 'public',
};

export interface MessageContent {
    text: string;
    attachment?: { name: string; file: File; aspectRatio?: number };
    imageSrc?: string;
}

interface ExistingSessionProps {
    chatSession: ChatSessionKey;
    afterSend?: () => void;
    preventInitialFocus?: boolean;
    onInputResize?: () => void;
    threadMessageKey?: ChatMessageKey;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

interface NewSessionProps {
    onSend: (
        text: string,
        attachment?: { name: string; file: File },
        alsoAsSMS?: boolean,
    ) => void;
    preventInitialFocus?: boolean;
    totalNewChatMembers?: number;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

// One source for the hint and the name, so the two cannot drift. A screen
// reader can list a page's fields on their own, away from the thread they sit
// in, where a bare "Message" says nothing about where the text is going -- the
// same reason a row's menu is named after its row.
//
// The placeholder stays absent until the session has a name, as it was; the
// name falls back to the bare word instead, since no name at all is worse
// than one without the subject.
const getComposerLabels = (
    threadMessageKey: ChatMessageKey | undefined,
    sessionName: string | undefined,
    i18next: I18n,
) => {
    if (threadMessageKey) {
        const replyToThread = i18next.t('ThreadMessages.ReplyThread');

        return { placeholder: replyToThread, ariaLabel: replyToThread };
    }

    const sendTo = i18next.t('message.input.send_to');

    if (!sessionName) {
        return { placeholder: undefined, ariaLabel: sendTo };
    }

    const sendToSession = `${sendTo} ${sessionName}`;

    return { placeholder: sendToSession, ariaLabel: sendToSession };
};

const MessageInput = (props: ExistingSessionProps | NewSessionProps) => {
    const { i18next } = useAppContext();
    const { closeModal, openModal } = useModal();
    const { substitutePlaceholders } = usePremadeAnswers();

    const chatSession = 'chatSession' in props ? props.chatSession : undefined;
    const threadMessageKey =
        'threadMessageKey' in props ? props.threadMessageKey : null;
    const newSession = 'onSend' in props;
    const preventInitialFocus = 'preventInitialFocus' in props;
    const onInputResize =
        'onInputResize' in props ? props.onInputResize : undefined;
    const totalNewChatMembers =
        'totalNewChatMembers' in props ? props.totalNewChatMembers : 0;
    const videoDark = !!props.videoDark;

    const ownUserHasCapabilitySms = useAppSelector((state) =>
        selectOwnUserHasCapability(state, Capabilities.SMS),
    );
    const ownUserHasCapabilityGroupSms = useAppSelector((state) =>
        selectOwnUserHasCapability(state, Capabilities.GROUP_SMS),
    );

    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const dispatch = useAppDispatch();

    const { ref: inputRef, height: inputRefHeight } = useResizeObserver();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const session = useAppSelector((state) =>
        state.chats.getIn(['sessions', chatSession]),
    );

    const extensions = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );

    const otherPartExtensionKey: ExtensionKey | null =
        session?.get('members')?.size === 2
            ? session
                  .get('members')
                  .find((m) => m.get('extensionKey') !== ownExtensionKey)
                  .get('extensionKey')
            : null;

    const otherPartExtension = useAppSelector((state) =>
        state.extensions.getIn(['extensions', otherPartExtensionKey]),
    );

    const clientKey = useAppSelector((state) =>
        state.chats.getIn(['client', 'key']),
    );

    const roomType = useAppSelector((state) =>
        state.chats.getIn(['sessions', chatSession, 'roomType'], 'session'),
    );

    const composerStatePath = threadMessageKey
        ? ['composer', chatSession, threadMessageKey]
        : ['composer', chatSession];

    const attachment = useAppSelector((state) =>
        state.chats.getIn([...composerStatePath, 'attachment'], null),
    );

    const messageText = useAppSelector((state) =>
        state.chats.getIn([...composerStatePath, 'text'], ''),
    );

    const replyToMessage = useAppSelector((state) =>
        state.chats.getIn(['composer', chatSession, 'quotedMessage']),
    );

    const defaultContent: MessageContent = {
        text: '',
        imageSrc: '',
    };

    const [messageContent, setMessageContent] =
        useState<MessageContent>(defaultContent);

    const [cursorPosition, setCursorPosition] = useState<number | null>(null);

    useEffect(() => {
        setMessageContent((prev) => ({
            ...prev,
            text: messageText,
            attachment,
        }));

        if (attachment) {
            setImageSource(attachment.file);
        }

        const textArea = textAreaRef?.current;
        if (!preventInitialFocus) {
            textArea?.focus();
        }

        return () => {
            dispatch(
                messageCompose(
                    chatSession,
                    !preventInitialFocus ? textArea?.value : '',
                    threadMessageKey,
                ),
            );
        };
    }, [chatSession, attachment, threadMessageKey]);

    useEffect(() => {
        textAreaRef.current.focus();
    }, [replyToMessage]);

    useEffect(() => onInputResize && onInputResize(), [inputRefHeight]);

    useAutosizeTextArea(textAreaRef, messageContent.text);

    // Mention inserter and pre-made answer state is reset when we change chat.
    const [mentionInserterState, setMentionInserterState] = useState(
        getInitialMentionInserterState(),
    );
    const [premadeAnswerInserterState, setPremadeAnswerInserterState] =
        useState(getInitialPremadeAnswerInserterState());
    useEffect(() => {
        setMentionInserterState(getInitialMentionInserterState());
        setPremadeAnswerInserterState(getInitialPremadeAnswerInserterState());
        return closeModal();
    }, [chatSession]);

    const updateMessageAndPosition = (text: string, index: number) => {
        setMessageContent((prev) => ({ ...prev, text }));
        setCursorPosition(index);
    };

    useEffect(() => {
        if (cursorPosition !== null && textAreaRef.current) {
            textAreaRef.current.focus();
            textAreaRef.current.setSelectionRange(
                cursorPosition,
                cursorPosition,
            );
            setCursorPosition(null);
        }
    }, [cursorPosition, messageContent.text]);

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (
            offerKeyEventToMessageInserter(
                event,
                mentionInserterState,
                setMentionInserterState,
                messageContent.text,
                updateMessageAndPosition,
            ) ||
            offerKeyEventToPremadeAnswerInserter(
                event,
                premadeAnswerInserterState,
                setPremadeAnswerInserterState,
                updateMessageAndPosition,
                substitutePlaceholders,
            )
        ) {
            return;
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            handleSubmit(event);
        }
        if (isCmdOrCtrlPressed(event) && event.key === 'e' && event.shiftKey) {
            event.preventDefault();
            handleEmailSubmit();
        }
    };

    const handleSend = (alsoAsSMS: boolean = false) => {
        if ('onSend' in props) {
            props.onSend(
                messageContent.text,
                messageContent.attachment,
                alsoAsSMS,
            );
            setMessageContent(defaultContent);
        } else {
            const mentions = createMentionDTOsFromText(
                messageContent.text,
                extensions,
            );

            if (alsoAsSMS) {
                dispatch(attachmentCompose(chatSession, null));
                dispatch(messageCompose(chatSession, ''));
            }

            if (attachment) {
                attachment.name = replaceWhitespaces(attachment.name, '_');

                dispatch(
                    prepareAttachment(
                        chatSession,
                        clientKey,
                        messageContent.text.trimEnd(),
                        mentions,
                        attachment,
                        replyToMessage?.get('key'),
                        threadMessageKey,
                    ),
                );
                dispatch(removeQuotedMessage(chatSession));
            } else {
                dispatch(
                    sendChatMessage(
                        chatSession,
                        clientKey,
                        messageContent.text.trimEnd(),
                        mentions,
                        null,
                        alsoAsSMS,
                        replyToMessage?.get('key'),
                        threadMessageKey,
                    ),
                );
                dispatch(removeQuotedMessage(chatSession));
            }

            if (!alsoAsSMS) {
                dispatch(
                    attachmentCompose(chatSession, null, threadMessageKey),
                );
                dispatch(messageCompose(chatSession, '', threadMessageKey));
            }

            setMessageContent(defaultContent);

            props.afterSend?.();
        }
    };

    const handleChatMessage = () => handleSend(false);
    const handleSmsMessage = () => handleSend(true);

    const handleEmailSubmit = () => {
        if (messageContent.text.trimEnd() && !messageContent.attachment) {
            handleEmailMessage();
        }
    };

    const handleEmailMessage = () => {
        if (hasEmailOption) {
            window.open(
                `mailto:${otherPartExtension.getIn(['contact', 'email'])}?&body=${encodeURIComponent(messageContent.text.trimEnd())}`,
                '_blank',
            );
            setMessageContent(defaultContent);
        }
    };

    const hasEmailOption =
        roomType === 'session' &&
        otherPartExtension?.getIn(['contact', 'email']);

    const setImageSource = (file: File) => {
        if (
            getAttachmentTypeFromExtension(file.name) !== ChatAttachments.PHOTO
        ) {
            setMessageContent((prev) => {
                return { ...prev, imageSrc: '' };
            });
        } else {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onloadend = () => {
                setMessageContent((prev) => {
                    if (reader.result) {
                        return { ...prev, imageSrc: reader.result.toString() };
                    }
                    return {
                        ...prev,
                        attachment: null,
                        imageSrc: '',
                    };
                });
            };
        }
    };

    const handleAddAttachment = (file: File) => {
        if (file) {
            if (file.size > MAX_ALLOWED_ATTACHMENT_SIZE) {
                openModal('messageAttachmentTooLarge');
                return;
            }

            if (chatSession) {
                dispatch(
                    attachmentCompose(
                        chatSession,
                        { name: file.name, file },
                        threadMessageKey,
                    ),
                );
            }
            textAreaRef.current.focus();
        }
    };

    const handleRemoveAttachment = () => {
        if (chatSession) {
            dispatch(attachmentCompose(chatSession, null));
        }
        setMessageContent((prev) => ({
            ...prev,
            attachment: undefined,
            imageSrc: '',
        }));
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const file = e.dataTransfer.files[0];

        if (file) {
            handleAddAttachment(file);
        }
    };

    const handlePasteImage = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const items = e.clipboardData.items;

        let file = null;
        for (const item of Object.values(items)) {
            if (item.type.indexOf('image') === 0) {
                file = item.getAsFile();
            }
        }

        if (file) {
            handleAddAttachment(file);
            e.preventDefault();
        }
    };

    const handleSubmit = (
        event: KeyboardEvent<HTMLTextAreaElement> | FormEvent<HTMLFormElement>,
    ) => {
        event.preventDefault();

        if ((event as KeyboardEvent).nativeEvent?.isComposing) {
            return;
        }

        if (messageContent.text.trimEnd() || messageContent.attachment) {
            handleChatMessage();
        }
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const text = e.target?.value;

        setMessageContent((prev) => ({
            ...prev,
            text,
        }));

        dispatch(messageCompose(chatSession, text, threadMessageKey));

        setMentionInserterState(
            getNextMentionInserterState(mentionInserterState, e.target),
        );

        setPremadeAnswerInserterState(
            getNextPremadeAnswerInserterState(
                premadeAnswerInserterState,
                e.target,
            ),
        );
    };

    const members = session
        ?.get('members')
        ?.filter((member) => member.get('contact'));

    const memberExtensionKeys = members
        ?.map((member) => member.get('extensionKey'))
        .toSet();

    const filteredExtensions = extensions.filter((extension) =>
        memberExtensionKeys?.contains(extension.get('key')),
    );

    const hasSMS = () => {
        if (replyToMessage || threadMessageKey) {
            return false;
        }

        if ([roomTypes.public, roomTypes.room].includes(roomType)) {
            return ownUserHasCapabilityGroupSms;
        }

        if (roomType === roomTypes.session) {
            if (session?.get('members')?.size > 2) {
                return ownUserHasCapabilityGroupSms;
            }

            if (totalNewChatMembers > 1) {
                return ownUserHasCapabilityGroupSms;
            }

            return ownUserHasCapabilitySms;
        }

        return false;
    };

    const { placeholder, ariaLabel } = getComposerLabels(
        threadMessageKey,
        session?.get('name'),
        i18next,
    );

    return (
        <div
            className="relative w-full max-w-2xl"
            ref={inputRef}
            onDrop={handleDrop}
        >
            {!threadMessageKey && (
                <QuotedMessage
                    chatSessionKey={session?.get('key')}
                    videoDark={videoDark}
                />
            )}
            <PremadeAnswerInserter
                state={premadeAnswerInserterState}
                updateState={setPremadeAnswerInserterState}
                updateMessage={updateMessageAndPosition}
            />
            <PremadeAnswersManager
                message={messageContent.text}
                updateMessage={updateMessageAndPosition}
            />

            {messageContent.attachment && (
                <AttachmentPreview
                    imageSrc={messageContent.imageSrc}
                    fileName={messageContent.attachment.name}
                    handleRemoveAttachment={handleRemoveAttachment}
                />
            )}

            <form onSubmit={handleSubmit}>
                <div
                    className={twMerge(
                        'relative flex w-full items-start gap-x-1 p-2 first:rounded-t-sm last:rounded-b-sm sm:gap-x-3 sm:p-3',
                        videoDark ? 'bg-gray-700' : 'bg-gray-200',
                    )}
                >
                    <AddButton
                        ref={textAreaRef}
                        roomType={roomType}
                        handleAddAttachment={handleAddAttachment}
                        disablePremadeAnswers={!!threadMessageKey}
                        chatSessionKey={chatSession}
                        videoDark={videoDark}
                    />
                    <div className="relative flex h-full min-w-0 flex-1">
                        {chatSession && (
                            <MentionInserter
                                state={mentionInserterState}
                                updateState={setMentionInserterState}
                                message={messageContent.text}
                                updateMessage={updateMessageAndPosition}
                                sessionKey={chatSession}
                            />
                        )}
                        <textarea
                            id="chat-message"
                            data-test-id="message-input"
                            className={twMerge(
                                'h-fit max-h-24 w-full min-w-0 flex-1 resize-none overflow-y-scroll bg-transparent pt-1 text-base text-wrap placeholder:truncate focus:border-transparent focus:ring-0 focus:outline-hidden',
                                videoDark
                                    ? 'text-white placeholder:text-gray-400'
                                    : 'text-gray-800 placeholder:text-gray-500',
                                threadMessageKey ? 'min-h-[32px]' : '',
                            )}
                            onChange={handleTextChange}
                            onPaste={handlePasteImage}
                            onKeyDown={handleKeyDown}
                            ref={textAreaRef}
                            rows={1}
                            aria-label={ariaLabel}
                            value={messageContent.text}
                            placeholder={placeholder}
                        />
                    </div>

                    <Suspense fallback={<></>}>
                        <EmojiSearch
                            textAreaRef={textAreaRef}
                            messageContent={messageContent}
                            updateMessage={updateMessageAndPosition}
                        />
                    </Suspense>

                    <div className="flex items-center gap-x-1 sm:gap-x-2">
                        <Suspense fallback={<></>}>
                            <EmojiPicker
                                textAreaRef={textAreaRef}
                                messageContent={messageContent}
                                updateMessage={updateMessageAndPosition}
                                videoDark={videoDark}
                            />
                        </Suspense>

                        {(session || newSession) && (
                            <SendButton
                                message={messageContent}
                                videoDark={videoDark}
                                handleEmailMessage={
                                    hasEmailOption &&
                                    !replyToMessage &&
                                    !threadMessageKey
                                        ? handleEmailMessage
                                        : undefined
                                }
                                handleSend={
                                    filteredExtensions.size > 1 ||
                                    totalNewChatMembers > 0
                                        ? handleChatMessage
                                        : undefined
                                }
                                handleSmsMessage={
                                    hasSMS() ? handleSmsMessage : undefined
                                }
                            />
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default MessageInput;
